import { computed, inject, Injectable } from '@angular/core';

import { BrandedIdType } from '../../customer-creation/model/branded-id-type';
import { PreregistrationResponse } from '../clients/gcdm/model/response.model';
import { ConsentMapper } from '../mapper/consent.mapper';
import { ConsentMetadata, ConsentSelection } from '../model/consent-checkbox';
import { NSCConsent, NSCConsentSelection, NSCConsentVariant } from '../model/nsc-consent';
import { ConsentService } from './consent.service';
import { GcdmLanguageProvider } from './gcdm-language.provider';

@Injectable({
  // not providedIn: 'root', so that the consents cannot be shared between different customers
  providedIn: null,
})
export class ConsentProvider {
  private readonly consentService = inject(ConsentService);
  private readonly consentMapper = inject(ConsentMapper);
  private readonly gcdmLanguageProvider = inject(GcdmLanguageProvider);

  readonly metadata = computed((): ConsentMetadata | undefined => {
    const consentData = this.consentService.nscConsentData();
    if (!consentData) return undefined;
    return {
      majorVersion: consentData.majorVersion,
      minorVersion: consentData.minorVersion,
      date: consentData.date,
    };
  });

  private readonly nscConsentsForCurrentLanguage = computed(() => {
    return this.consentService.nscConsentData()?.languages.get(this.gcdmLanguageProvider.resolvedConsentLanguage());
  });

  readonly nscLegalText = computed(() => this.nscConsentsForCurrentLanguage()?.legalText ?? {});

  readonly allNscConsents = computed(() => this.nscConsentsForCurrentLanguage()?.consents);

  readonly nonImplicitNscConsents = computed(() =>
    this.allNscConsents()
      ?.map(
        (group): NSCConsent => ({
          ...group,
          usages: group.usages.filter((usage) => usage.usageVariant !== NSCConsentVariant.IMPLICIT),
        }),
      )
      .filter((group) => group.usages.length !== 0),
  );

  async loadConsentSelection(gcid: string): Promise<NSCConsentSelection> {
    if (!gcid) {
      return {};
    }
    return this.consentService.getConsentSelection(gcid);
  }

  static chosenUsages(this: void, selection: ConsentSelection): string[];
  static chosenUsages(this: void, selection: ConsentSelection | undefined): string[] | undefined;
  static chosenUsages(this: void, selection: ConsentSelection | undefined) {
    return (
      selection &&
      Object.keys(selection)
        .filter((key) => selection[key])
        .sort((a, b) => a.localeCompare(b))
    );
  }

  async confirmConsentSelection(gcid: string, consentSelection: ConsentSelection, addImplicits: boolean) {
    const consents = this.nscConsentsForCurrentLanguage();
    if (!consents) {
      throw new Error('#CDFTF0037 Tried to save consents before they have been loaded');
    }
    if (!gcid) {
      throw new Error('#CDFTF0039 Tried to save consents without a gcid');
    }
    return await this.consentService.confirmConsentSelection(gcid, consentSelection, consents, addImplicits);
  }

  async preregister(
    gcid: string,
    emailAddress: string,
    brand: BrandedIdType,
  ): Promise<PreregistrationResponse | undefined> {
    if (brand === BrandedIdType.NONE) {
      return;
    }
    const metadata = this.metadata();
    if (!metadata) {
      throw new Error('#CDFTF0054 Tried to preregister before the consents have been loaded');
    }
    if (!gcid) {
      throw new Error(`#CDFTF0055 Tried to preregister without a gcid, ${JSON.stringify(emailAddress)}`);
    }

    return await this.consentService.preregister({
      gcid,
      brand,
      emailAddress,
      language: this.gcdmLanguageProvider.resolvedConsentLanguage(),
    });
  }
}
